/**
 * Compact child selector used across rule-editing pages.
 *
 * Renders inline as a row of "chips" — one per child. Default-selects the
 * first child when none is chosen so callers don't need to special-case the
 * "no selection" state.
 */
import { useEffect } from 'react';
import type { Child } from '@/api/users';
import { cn } from '@/lib/utils';

export function ChildPicker({
  children,
  selectedId,
  onSelect,
}: {
  children: Child[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  useEffect(() => {
    if (!selectedId && children.length > 0) {
      onSelect(children[0]!.id);
    }
  }, [children, selectedId, onSelect]);

  if (children.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" role="tablist">
      {children.map((c) => {
        const active = c.id === selectedId;
        return (
          <button
            key={c.id}
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(c.id)}
            className={cn(
              'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
              active
                ? 'border-[color:var(--kz-color-brand-primary)] bg-[color:var(--kz-color-brand-primary)]/10 text-[color:var(--kz-color-brand-primary)] font-medium'
                : 'border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)]',
            )}
          >
            <span
              className="size-2.5 rounded-full"
              style={{ backgroundColor: c.color ?? '#4F6BFF' }}
            />
            {c.name}
          </button>
        );
      })}
    </div>
  );
}
